/**
 * `/pools/search`, the web pool search screen (ISSUE-199, ISSUE-026 POOLS-7).
 * A fan types part of a pool name or a whole Pool ID and gets back the pools
 * the server lets them see.
 *
 * The file is named `pools_.search` so this screen stays out of `/pools`'s
 * two-pane tree (no Outlet), the same convention `settings_.blocked.tsx`
 * uses. The Pools list carries a "Find a pool" control that navigates here,
 * so this is a screen a fan reaches by pressing something, not a URL only a
 * developer knows. A pane mounted on no route is not shipped.
 *
 * url-state.mdc rule 3: the typed query is URL state, held in `?q=` here and
 * passed down, so a refresh, the browser back button and a pasted link all
 * keep the search. The key is `q` rather than the rule's illustrative
 * `searchQuery` to match `/pools`'s own `?q=` in the same section, since a
 * fan moving between the two should not meet two names for one idea. The
 * sport, league and pool type filters (R4) are URL state by the same rule,
 * in `?sportKey=`, `?leagueKey=` and `?contestType=`.
 *
 * server-resolves-client-renders.mdc: the server applied the query text, the
 * three filters and the visibility rule before the response left it. This
 * route re-sorts nothing, re-filters nothing, and hides no row.
 *
 * A public result opens the join preview in `?dialog=join-pool&poolId=`
 * (ISSUE-1039); only an invited row or a private member row navigates away.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { PoolSearchJoinDialog } from "../features/pools/PoolSearchJoinDialog";
import {
  POOL_SEARCH_JOIN_DIALOG,
  resolvePoolSearchSelection,
} from "../features/pools/poolSearchJoinFlow";
import { PoolSearchPane } from "../features/pools/PoolSearchPane";
import { coerceOptionalString } from "../routeState/searchParams";
import { useOverlay } from "../routeState/useOverlay";
import { BACK_LINK_CLASS } from "../shell/BackLink";
import { SectionScreen } from "../shell/SectionScreen";

/** Matches the mobile screen's own title, `pools/_layout.tsx`. */
export const POOL_SEARCH_HEADING = "Find a pool";

export const POOL_SEARCH_BACK_LABEL = "Back to your pools";

export type typePoolSearchScreenSearch = {
  q?: string | undefined;
  sportKey?: string | undefined;
  leagueKey?: string | undefined;
  contestType?: string | undefined;
  /** ISSUE-1039. `join-pool` while the search join preview is open. */
  dialog?: string | undefined;
  /** ISSUE-1039. The pool the join preview is for. */
  poolId?: string | undefined;
};

/**
 * The three filter keys are named for the wire fields they become
 * (`/pool/search`'s `sportKey`, `leagueKey` and `contestType`), so a URL in a
 * bug report says what it narrowed by without anyone opening this file.
 *
 * None of them is validated against the catalog here. A key the catalog does
 * not carry simply returns no pools, which is the honest answer, and dropping
 * it instead would silently widen a shared link back to every pool.
 */
export function validatePoolSearchScreenSearch(
  search: Record<string, unknown>,
): typePoolSearchScreenSearch {
  return {
    q: coerceOptionalString(search.q),
    sportKey: coerceOptionalString(search.sportKey),
    leagueKey: coerceOptionalString(search.leagueKey),
    contestType: coerceOptionalString(search.contestType),
    dialog: coerceOptionalString(search.dialog),
    poolId: coerceOptionalString(search.poolId),
  };
}

const PoolSearchScreen = (): ReactNode => {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/pools/search" });
  const overlay = useOverlay();
  const joinPoolId = overlay.isOpen("dialog", POOL_SEARCH_JOIN_DIALOG)
    ? search.poolId
    : undefined;

  return (
    <SectionScreen heading={POOL_SEARCH_HEADING}>
      <Link
        to="/pools"
        className={BACK_LINK_CLASS}
        data-testid="pool-search-back"
      >
        {POOL_SEARCH_BACK_LABEL}
      </Link>
      <PoolSearchPane
        query={search.q ?? ""}
        // `replace`, so a fan who typed six letters presses back once to leave
        // the screen rather than six times to unwind their own typing. Same
        // choice `/pools` makes for its own `?q=`.
        onQueryChange={(q) => {
          void navigate({
            search: (prev) => ({ ...prev, q: q === "" ? undefined : q }),
            replace: true,
          });
        }}
        filters={{
          sportKey: search.sportKey ?? null,
          leagueKey: search.leagueKey ?? null,
          contestType: search.contestType ?? null,
        }}
        // No `replace` here, unlike the query above. Typing is a stream of
        // keystrokes nobody wants in their history; choosing a filter is one
        // deliberate press, so browser back undoing it one press at a time is
        // what a fan expects. A cleared filter drops its key out of the URL
        // rather than sitting there empty.
        onFiltersChange={(next) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              sportKey: next.sportKey ?? undefined,
              leagueKey: next.leagueKey ?? undefined,
              contestType: next.contestType ?? undefined,
            }),
          });
        }}
        onSelectPool={(result) => {
          const selection = resolvePoolSearchSelection(result);
          switch (selection.kind) {
            case "invites":
              // R5. A result the fan holds an invitation to opens the invitation,
              // never the pool: they are not a member yet, so the pool home would
              // refuse them. The `/pool/search` response carries no invite id, so
              // the section root is the destination rather than the one waiting
              // invitation; giving `/invites` that id is ISSUE-067's work. Same
              // accepted partial the mobile screen ships.
              void navigate({ to: "/invites" });
              return;
            case "openPool":
              // A private row reaches search only for a member or an invitee (ISSUE-1039 OD2).
              void navigate({
                to: "/pools",
                search: { poolId: selection.poolId },
              });
              return;
            case "joinPreview":
              // A public pool previews before it opens, so a non-member never meets the no-access state (ISSUE-1039).
              overlay.open({
                kind: "dialog",
                name: POOL_SEARCH_JOIN_DIALOG,
                params: { poolId: selection.poolId },
              });
              return;
          }
        }}
      />
      {joinPoolId === undefined ? null : (
        <PoolSearchJoinDialog
          key={joinPoolId}
          poolId={joinPoolId}
          onClose={() => {
            overlay.close({ kind: "dialog", clearParams: ["poolId"] });
          }}
          // `replace`, so browser back from the pool returns to the search
          // rather than to a dialog that already did its job.
          onGoToPool={(poolId) => {
            void navigate({ to: "/pools", search: { poolId }, replace: true });
          }}
        />
      )}
    </SectionScreen>
  );
};

export const Route = createFileRoute("/pools_/search")({
  beforeLoad: requireSignedIn,
  validateSearch: validatePoolSearchScreenSearch,
  component: PoolSearchScreen,
});
